import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import { BRAND_BLUE } from "../../shared/pricingHelpers";

export interface TopupPresetValues {
  amount: number | "";
  currency: string;
  label: string;
  display_order: number | "";
  is_active: boolean;
}

const EMPTY: TopupPresetValues = {
  amount: "",
  currency: "USD",
  label: "",
  display_order: 0,
  is_active: true,
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<TopupPresetValues> | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const schema = Yup.object().shape({
  amount: Yup.number()
    .typeError("Amount is required")
    .required("Amount is required")
    .moreThan(0, "Must be > 0"),
  currency: Yup.string().oneOf(["USD", "INR"]).required("Currency is required"),
});

const buildPayload = (v: TopupPresetValues) => ({
  amount: Number(v.amount),
  currency: v.currency,
  label: v.label.trim() === "" ? null : v.label.trim(),
  display_order: v.display_order === "" ? 0 : Number(v.display_order),
  is_active: v.is_active,
});

const TopupPresetsForm: React.FC<Props> = ({
  open,
  mode,
  initial,
  saving,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<TopupPresetValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY, ...(initial || {}) } as TopupPresetValues);
      setErrors({});
    }
  }, [open, initial]);

  const set = (name: keyof TopupPresetValues, value: any) =>
    setValues((p) => ({ ...p, [name]: value }));

  const handleSubmit = async () => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      onSubmit(buildPayload(values));
    } catch (err: any) {
      const e: Record<string, string> = {};
      err.inner?.forEach((x: any) => (e[x.path] = x.message));
      setErrors(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {mode === "create" ? "Add Top-up Preset" : "Edit Top-up Preset"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Amount"
              type="number"
              value={values.amount}
              onChange={(e) =>
                set("amount", e.target.value === "" ? "" : Number(e.target.value))
              }
              error={Boolean(errors.amount)}
              helperText={errors.amount}
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="topup-currency">Currency</InputLabel>
              <Select
                labelId="topup-currency"
                label="Currency"
                value={values.currency}
                onChange={(e) => set("currency", e.target.value)}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Label"
            value={values.label}
            onChange={(e) => set("label", e.target.value)}
            helperText="Optional, e.g. popular"
            size="small"
            fullWidth
          />
          <TextField
            label="Display Order"
            type="number"
            value={values.display_order}
            onChange={(e) =>
              set("display_order", e.target.value === "" ? "" : Number(e.target.value))
            }
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={values.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "#0445a3" },
          }}
        >
          {mode === "create" ? "Create" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopupPresetsForm;
